/* eslint-disable react/no-unknown-property */
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { Sector } from "recharts";

import { roundOff, getStockCompanyIcon } from "utils";
import { RADIAN } from "utils/constants";
import { PieChart } from "components/Chart";
import { HoldingsPieChartPlaceholder } from "./HoldingsPieChartPlaceholder";

const LabelContent = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
}) => {
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return percent > 5 ? (
    <text
      x={x}
      y={y}
      fill="white"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
    >
      {`${roundOff(percent * 100, 0)}%`}
    </text>
  ) : null;
};

const LegendContent = ({ payload }) => {
  return (
    <ul>
      {payload.map((entry, index) => (
        <li key={`item-${index}`}>
          <span
            className="line"
            style={{ backgroundColor: entry.payload.color }}
          ></span>
          {entry.value}
        </li>
      ))}
    </ul>
  );
};

const renderActiveShape = (props) => {
  const {
    cx,
    cy,
    innerRadius,
    outerRadius,
    startAngle,
    endAngle,
    fill,
    payload,
    value,
  } = props;

  const iconSize = 24;
  const textPadding = 5;

  return (
    <g>
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + 8}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
      />
      <g transform={`translate(${cx - 40}, ${cy - 30})`}>
        <image
          x={0}
          y={0}
          width={iconSize}
          height={iconSize}
          href={getStockCompanyIcon(payload.name)}
          onError={(e) => {
            e.target.style.display = "none";
          }}
        />
        <text
          x={iconSize + textPadding}
          y={iconSize / 2 + 3}
          dominantBaseline="middle"
          textAnchor="start"
          className="pie-label"
        >
          {payload.name}
        </text>
      </g>
      <text x={cx} y={cy} textAnchor="middle" fill="#000" className="pie-label">
        <tspan x={cx} dy="1.2em">
          {roundOff(value * 100)}%
        </tspan>
      </text>
    </g>
  );
};

export const HoldingsPieChart = ({
  data = [],
  showLegend = true,
  innerRadius = 60,
  isLoading,
  ...props
}) => {
  const [activeIndex, setActiveIndex] = useState(null);

  const onMouseOver = useCallback((data, index) => {
    setActiveIndex(index);
  }, []);

  const onMouseLeave = useCallback(() => {
    setActiveIndex(null);
  }, []);

  if (isLoading) {
    return <HoldingsPieChartPlaceholder />;
  }

  if (data.length === 0) {
    return null;
  }

  return (
    <div style={{ width: "100%" }} className="pie-chart-wrapper">
      <PieChart
        data={data}
        showLegend={showLegend}
        innerRadius={innerRadius}
        labelContent={LabelContent}
        legendContent={LegendContent}
        pieProps={{
          activeIndex,
          onMouseOver,
          onMouseLeave,
          activeShape: renderActiveShape,
          paddingAngle: 2,
        }}
        {...props}
      />
    </div>
  );
};

HoldingsPieChart.propTypes = {
  data: PropTypes.array,
  showLegend: PropTypes.bool,
  innerRadius: PropTypes.number,
  isLoading: PropTypes.bool,
};

LabelContent.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  midAngle: PropTypes.number,
  innerRadius: PropTypes.number,
  outerRadius: PropTypes.number,
  percent: PropTypes.number,
};

LegendContent.propTypes = {
  payload: PropTypes.array,
};
